import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {useSearchParams} from "react-router";
import {Template} from "../../../../../components/template/template.jsx";
import {Card} from "../../../../../components/card/card.jsx";
import Button from "../../../../../components/button/button.jsx";
import InputForm from "../../../../../components/input/input-form.jsx";
import InputDatetime from "../../../../../components/input/input-datetime.jsx";
import SelectComponent from "../../../../../components/select/select-component.jsx";
import {FaBan, FaEraser, FaPlus, FaSearch} from "react-icons/fa";
import {FaCalendarDays, FaCircleInfo, FaPenToSquare, FaTableList} from "react-icons/fa6";
import {AgGridReact} from "ag-grid-react";
import {keepPreviousData, useQuery} from "@tanstack/react-query";
import aggridUtils from "../../../../../utils/aggrid-utils.js";
import {navigateHook} from "../../../../../hooks/use-navigate.js";
import {useGlobalKey} from "../../../../../hooks/use-global-key.js";
import useHasPermission from "../../../../../hooks/use-has-permission.js";
import {PermissionEnum} from "../../../../../utils/enums/permission-enum.js";
import citizenScheduledVisitService from "../../../../../services/web/citizen-scheduled-visit-service.js";
import citizenService from "../../../../../services/web/citizen-service.js";
import CitizenScheduledVisitEditModal from "./components/citizen-scheduled-visit-edit-modal.jsx";
import CitizenScheduledVisitExecutionModal from "./components/citizen-scheduled-visit-execution-modal.jsx";
import CitizenScheduledVisitCalendar from "./components/citizen-scheduled-visit-calendar.jsx";
import {format} from "date-fns";
import {
    composeAddress,
    SCHEDULE_TYPE_OPTIONS,
    scheduleTypeLabel
} from "../utils/schedule-utils.js";
import CitizenScheduledVisitCancelModal from "./components/citizen-scheduled-visit-cancel-modal.jsx";
import {finishDashboardEdit} from "../../../../../utils/dashboard-edit-window.js";
import projectUtils from "../../../../../utils/project-utils.js";

const STATUS_OPTIONS = [
    {value: "OPEN", label: "Em aberto"},
    {value: "EXECUTED", label: "Realizado"},
    {value: "CANCELED", label: "Cancelado"},
];

const initialFilters = {
    citizen: [],
    type: "",
    status: [{value: "OPEN", label: "Em aberto"}, {value: "EXECUTED", label: "Realizado"}],
    startDate: "",
    endDate: "",
    address: "",
};

const CitizenScheduledVisitList = () => {
    const navigate = navigateHook();
    const hasPermission = useHasPermission();

    const buildFilter = (values) => {
        const filter = [];

        if (values.citizen.length) {
            filter.push({field: "citizenId", type: "in", value: values.citizen.map(c => c.value)});
        }

        if (values.type) {
            filter.push({field: "type", type: "=", value: values.type});
        }

        if (values.status.length) {
            filter.push({field: "status", type: "in", value: values.status.map(s => s.value)});
        }

        if (values.startDate) {
            filter.push({field: "scheduleWindowStartDt", type: ">=", value: values.startDate});
        }

        if (values.endDate) {
            filter.push({field: "scheduleWindowStartDt", type: "<=", value: values.endDate});
        }

        if (values.address?.trim()) {
            filter.push({field: "address", type: "like", value: values.address.trim()});
        }

        return filter;
    };

    const [view, setView] = useState("table");
    const [filterValues, setFilterValues] = useState(initialFilters);
    const [filter, setFilter] = useState({
        filter: buildFilter(initialFilters),
        sort: [{field: "scheduleWindowStartDt", dir: "DESC"}],
        size: -1
    });

    const {data, refetch, isLoading} = useQuery({
        queryFn: () => citizenScheduledVisitService.list(filter).then(({data}) => data),
        queryKey: ["citizen-scheduled-visit-list", {filter}],
        placeholderData: keepPreviousData,
    });

    const {data: citizens} = useQuery({
        queryFn: () => citizenService.options(),
        queryKey: ["citizen-options"],
    });

    const [editId, setEditId] = useState(null);
    const [editOpen, setEditOpen] = useState(false);

    const [infoId, setInfoId] = useState(null);
    const [infoOpen, setInfoOpen] = useState(false);

    const [cancelId, setCancelId] = useState(null);
    const [cancelOpen, setCancelOpen] = useState(false);

    const openEdit = (row) => {
        setEditId(row.id);
        setEditOpen(true);
    };

    const openInfo = (row) => {
        setInfoId(row.id);
        setInfoOpen(true);
    };

    const openCancel = (row) => {
        setCancelId(row.id);
        setCancelOpen(true);
    };

    const openSchedule = (row) => {
        if (!row) return;

        if (row.status === "EXECUTED") {
            openInfo(row);
            return;
        }

        if (row.status === "CANCELED") {
            openCancel(row);
            return;
        }

        if (hasPermission(PermissionEnum.SOS_CITIZEN_SCHEDULED_VISIT_MANAGE)) openEdit(row);
    };

    const [searchParams] = useSearchParams();
    const redir = searchParams.get("redir");
    const handledUrlIdRef = useRef(false);

    useEffect(() => {
        const urlId = searchParams.get("schedule_id");
        if (handledUrlIdRef.current || !urlId || !data?.data) return;

        handledUrlIdRef.current = true;
        openSchedule(data.data.find(item => String(item.id) === String(urlId)));
    }, [data, searchParams]);

    const [columnDefs] = useState([
        {
            field: "scheduleWindowStartDt",
            headerName: "D/H do agendamento",
            flex: 1,
            valueFormatter: ({data: row}) => {
                if (!row?.scheduleWindowStartDt) return "";

                // o backend manda 2026-07-31T17:38:00.000+0000; sem o offset a string vira hora local
                const start = format(row.scheduleWindowStartDt.slice(0, 16), "dd/MM/yyyy HH:mm");
                const end = row.scheduleWindowEndDt
                    ? format(row.scheduleWindowEndDt.slice(0, 16), "HH:mm")
                    : null;

                return end ? `${start} - ${end}` : start;
            },
            comparator: aggridUtils.agGridSimpleDateComparator,
        },
        {
            field: "type",
            headerName: "Tipo",
            width: 110,
            valueFormatter: ({value}) => scheduleTypeLabel(value),
        },
        {field: "citizenName", headerName: "Nome do protegido", flex: 1, comparator: aggridUtils.nullLastComparator},
        {
            field: "address",
            headerName: "Endereço",
            flex: 2,
            valueGetter: ({data: row}) => composeAddress(row ?? {}),
        },
        {
            field: "status",
            headerName: "Status",
            width: 140,
            valueGetter: ({data: {status}}) => STATUS_OPTIONS.find(s => s.value === status)?.label,
        },
        {
            headerName: "Ações",
            field: "actions",
            width: 90,
            sortable: false,
            cellRenderer: ({data: row}) => {
                if (!row) return "";

                if (row.status === "EXECUTED") {
                    return (
                        <div className={"flex items-center justify-center h-full"}>
                            <Button className={"w-7.5! h-6.25! p-0!"} title={"Detalhes da finalização"} onClick={() => openInfo(row)}>
                                <FaCircleInfo/>
                            </Button>
                        </div>
                    )
                }

                if (row.status === "CANCELED") {
                    return (
                        <div className={"flex items-center justify-center h-full"}>
                            <Button className={"w-7.5! h-6.25! p-0!"} title={"Detalhes do cancelamento"} onClick={() => openCancel(row)}>
                                <FaBan/>
                            </Button>
                        </div>
                    )
                }

                if (!hasPermission(PermissionEnum.SOS_CITIZEN_SCHEDULED_VISIT_MANAGE)) return "";

                return (
                    <div className={"flex items-center justify-center h-full"}>
                        <Button className={"w-7.5! h-6.25! p-0!"} title={"Editar"} onClick={() => openEdit(row)}>
                            <FaPenToSquare/>
                        </Button>
                    </div>
                )
            }
        },
    ]);

    const gridRef = useRef(undefined);

    const theme = useMemo(() => aggridUtils.generalTheme(), []);

    const handleInput = ({target: {id, value}}) => setFilterValues(prev => ({...prev, [id]: value}));

    const handleSelect = (id, selected) => setFilterValues(prev => ({...prev, [id]: selected?.value ?? ""}));

    const apply = useCallback(() => {
        setFilter(prev => ({...prev, page: 1, filter: buildFilter(filterValues)}));
    }, [filterValues]);

    const clear = useCallback(() => {
        setFilterValues(initialFilters);
        setFilter({
            filter: buildFilter(initialFilters),
            sort: [{field: "scheduleWindowStartDt", dir: "DESC"}],
            size: -1
        });
    }, []);

    useGlobalKey("Enter", apply, !editOpen && !infoOpen);
    useGlobalKey("Escape", clear, !editOpen && !infoOpen);

    const openDispatch = (dispatchId) => {
        navigate(`/despacho/dispatch/form/${dispatchId}`, true);
    }

    return (
        <>
            <Template isLoading={isLoading}>
            <Template.Header>
                <Template.Title>Agendamentos</Template.Title>

                <div className={"flex items-center gap-3"}>
                    <div className={"flex bg-gray-100 border border-gray-300 rounded-md p-0.5"}>
                        {
                            [
                                {key: "table", label: "Tabela", Icon: FaTableList},
                                {key: "calendar", label: "Calendário", Icon: FaCalendarDays},
                            ].map(({key, label, Icon}) => (
                                <button
                                    key={key}
                                    type="button"
                                    onClick={() => setView(key)}
                                    className={[
                                        "flex items-center gap-1.5 px-3 py-1 rounded text-[13px] font-semibold cursor-pointer transition-colors",
                                        view === key ? "bg-white text-[#063A6C] shadow-sm" : "text-gray-500 hover:text-gray-700"
                                    ].join(" ")}
                                >
                                    <Icon size={13}/> {label}
                                </button>
                            ))
                        }
                    </div>

                    <Button target={"primary"} onClick={() => navigate("/sos/citizen-scheduled-visit/form")}>
                        <FaPlus/> Novo agendamento
                    </Button>
                </div>
            </Template.Header>

            <Template.Body className={"space-y-2"}>
                <Card>
                    <Card.Body className={"grid grid-cols-12 gap-4"}>
                        <SelectComponent
                            classNameWrapper={"col-span-4"}
                            value={filterValues.citizen}
                            options={citizens ?? []}
                            label={"Protegido"}
                            onChange={(value) => setFilterValues(prev => ({...prev, citizen: value}))}
                            isMulti
                        />

                        <SelectComponent
                            classNameWrapper={"col-span-2"}
                            value={filterValues.type}
                            options={SCHEDULE_TYPE_OPTIONS}
                            label={"Tipo"}
                            onChange={(value) => handleSelect("type", value)}
                        />

                        <SelectComponent
                            classNameWrapper={"col-span-2"}
                            value={filterValues.status}
                            options={STATUS_OPTIONS}
                            label={"Status"}
                            onChange={(value) => setFilterValues(prev => ({...prev, status: value}))}
                            isMulti
                        />

                        <div className={"col-span-4 flex items-stretch"}>
                            <div className="relative flex-1">
                                <InputDatetime
                                    classNameWrapper={"right-date-input"}
                                    id={"startDate"}
                                    label={"Agendamento entre"}
                                    dateFormat={"LL"}
                                    returnDateObj={false}
                                    selected={filterValues.startDate}
                                    onChange={(value) => setFilterValues(prev => ({...prev, startDate: value ?? ""}))}
                                />
                            </div>
                            <div className="border border-[#E5E7EB] px-4 py-2 text-sm leading-none">à</div>
                            <div className="relative flex-1">
                                <InputDatetime
                                    classNameWrapper={"left-date-input"}
                                    id={"endDate"}
                                    label={""}
                                    dateFormat={"LL"}
                                    returnDateObj={false}
                                    selected={filterValues.endDate}
                                    onChange={(value) => setFilterValues(prev => ({...prev, endDate: value ?? ""}))}
                                />
                            </div>
                        </div>

                        <div className={"col-span-12 flex justify-between gap-4"}>
                            <InputForm
                                classNameWrapper={"w-full"}
                                id={"address"}
                                label={"Endereço"}
                                value={filterValues.address}
                                onChange={handleInput}
                            />

                            <div className={"flex gap-2"}>
                                <Button target={"default"} onClick={clear}>
                                    <FaEraser/>
                                </Button>
                                <Button onClick={apply}>
                                    <FaSearch/>
                                </Button>
                            </div>
                        </div>
                    </Card.Body>
                </Card>

                {
                    view === "table" &&
                    <Card>
                        <Card.Body>
                            <div className={"h-[calc(100vh-201px)]"}>
                                <AgGridReact
                                    ref={gridRef}
                                    rowData={data?.data ?? []}
                                    columnDefs={columnDefs}
                                    pagination
                                    paginationPageSize={100}
                                    paginationPageSizeSelector={[20, 50, 100, 500]}
                                    animateRows={false}
                                    suppressCellFocus={true}
                                    gridOptions={{localeText: aggridUtils.localization()}}
                                    theme={theme}
                                />
                            </div>
                        </Card.Body>
                    </Card>
                }

                {
                    view === "calendar" &&
                    <Card>
                        <Card.Body>
                            <div className={"h-[calc(100vh-201px)]"}>
                                <CitizenScheduledVisitCalendar
                                    rows={data?.data ?? []}
                                    onEventClick={openSchedule}
                                />
                            </div>
                        </Card.Body>
                    </Card>
                }
            </Template.Body>
            </Template>

            <CitizenScheduledVisitEditModal
                id={editId}
                open={editOpen}
                setOpen={setEditOpen}
                onChanged={refetch}
                onSaved={() => finishDashboardEdit(redir, navigate, "Visita programada salva com sucesso.")}
                openDispatch={openDispatch}
            />

            <CitizenScheduledVisitExecutionModal
                id={infoId}
                open={infoOpen}
                setOpen={setInfoOpen}
                openDispatch={openDispatch}
            />

            <CitizenScheduledVisitCancelModal
                id={cancelId}
                open={cancelOpen}
                setOpen={setCancelOpen}
                openDispatch={openDispatch}
            />
        </>
    );
};

export default CitizenScheduledVisitList;
